import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Clear,
  Delete as DeleteIcon,
  FilterList,
  MonetizationOn as MonetizationOnIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Undo as UndoIcon,
} from "@material-ui/icons";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";
import dayjs from "dayjs";

// RTK Query
import { useObtenerAbonosPendientesQuery } from "src/services/conciliacionApi";

// Helpers
import {
  formatearMonto,
  formatearRutDisplay,
  getBancoConfig,
} from "src/utils/conciliacionHelpers";

// Componentes
import SearchNotFound from "../SearchNotFound";
import { TablaHead } from "../_dashboard/tablas";
import ModalAplicarAbono from "./ModalAplicarAbono";
import ModalCerrarAbono from "./ModalCerrarAbono";
import { formatearFechaDisplay } from "src/utils/formatTime";

// ==================== CONFIGURACIÓN TABLA ====================

const TABLE_HEAD = [
  { id: "fecha_concilia", label: "Fecha Conciliación", alignRight: false },
  { id: "banco", label: "Banco", alignRight: false },
  { id: "rut_ordenante", label: "RUT Ordenante", alignRight: false },
  { id: "glosa", label: "Glosa", alignRight: false },
  { id: "monto_movimiento", label: "Monto Movimiento", alignRight: true },
  { id: "monto_abono", label: "Abono Disponible", alignRight: true },
  { id: "acciones", label: "Acciones", alignRight: false },
];

// ==================== OPCIONES DE FILTROS ====================

const opcionesEstadoAbono = [
  { value: "PENDIENTE", label: "Pendientes" },
  { value: "APLICADO", label: "Aplicados" },
  { value: "REEMBOLSADO", label: "Reembolsados" },
  { value: "DESCARTADO", label: "Descartados" },
  { value: "TODOS", label: "Todos" },
];

const opcionesBanco = [
  { value: "", label: "Todos" },
  { value: "SANTANDER", label: "Santander" },
  { value: "BCI", label: "BCI" },
];

// Umbral para considerar monto bajo
const UMBRAL_MONTO_BAJO = 1000;

// Estilo para filas con monto bajo
const estiloFilaMontoBajo = {
  backgroundColor: "#fbf4dc81",
};

// ==================== COMPONENTE PRINCIPAL ====================

export default function TablaAbonosPendientes() {
  // ==================== ESTADOS ====================

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filtros, setFiltros] = useState({
    estado_abono: "PENDIENTE",
    banco: "",
    fecha_desde: null,
    fecha_hasta: null,
    monto_min: "",
    monto_max: "",
  });

  // Modales
  const [modalAplicar, setModalAplicar] = useState({
    open: false,
    abono: null,
  });

  const [modalCerrar, setModalCerrar] = useState({
    open: false,
    abono: null,
  });

  // Menú de acciones
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAbono, setMenuAbono] = useState(null);

  // ==================== RTK QUERY ====================

  const { data, isLoading, isFetching, refetch } =
    useObtenerAbonosPendientesQuery({
      estado_abono: filtros.estado_abono || "PENDIENTE",
      banco: filtros.banco || undefined,
      fecha_desde: filtros.fecha_desde
        ? dayjs(filtros.fecha_desde).format("YYYY-MM-DD")
        : undefined,
      fecha_hasta: filtros.fecha_hasta
        ? dayjs(filtros.fecha_hasta).format("YYYY-MM-DD")
        : undefined,
      monto_min: filtros.monto_min || undefined,
      monto_max: filtros.monto_max || undefined,
      page: page + 1,
      limit: rowsPerPage,
    });

  const abonos = data?.data || [];
  const pagination = data?.pagination || { total: 0 };

  // ==================== HELPERS ====================

  const truncarTexto = (texto, maxLength = 40) => {
    if (!texto) return "-";
    return texto.length > maxLength
      ? `${texto.substring(0, maxLength)}...`
      : texto;
  };

  const contarFiltrosActivos = () => {
    let count = 0;
    if (filtros.estado_abono && filtros.estado_abono !== "PENDIENTE") count++;
    if (filtros.banco) count++;
    if (filtros.fecha_desde) count++;
    if (filtros.fecha_hasta) count++;
    if (filtros.monto_min) count++;
    if (filtros.monto_max) count++;
    return count;
  };

  const filtrosActivos = contarFiltrosActivos();

  const esMontoBajo = (monto) => parseFloat(monto) < UMBRAL_MONTO_BAJO;

  // ==================== HANDLERS ====================

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
    setPage(0);
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      estado_abono: "PENDIENTE",
      banco: "",
      fecha_desde: null,
      fecha_hasta: null,
      monto_min: "",
      monto_max: "",
    });
    setPage(0);
  };

  // Modales
  const handleAplicarAbono = (abono) => {
    setMenuAnchor(null);
    setModalAplicar({ open: true, abono });
  };

  const handleCloseModalAplicar = () => {
    setModalAplicar({ open: false, abono: null });
  };

  const handleCerrarAbono = (abono) => {
    setMenuAnchor(null);
    setModalCerrar({ open: true, abono });
  };

  const handleCloseModalCerrar = () => {
    setModalCerrar({ open: false, abono: null });
  };

  // Menú contextual
  const handleOpenMenu = (event, abono) => {
    setMenuAnchor(event.currentTarget);
    setMenuAbono(abono);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuAbono(null);
  };

  // ==================== RENDER ====================

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
      <Card>
        <Box sx={{ p: 2 }}>
          {/* ==================== FILTROS ==================== */}
          <Box sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <FilterList />
                <Typography variant="h6">Filtros</Typography>
                {filtrosActivos > 0 && (
                  <Chip
                    label={`${filtrosActivos} filtro(s) activo(s)`}
                    size="small"
                    color="primary"
                  />
                )}
              </Stack>

              <Tooltip title="Actualizar">
                <IconButton
                  onClick={refetch}
                  disabled={isLoading || isFetching}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            <Grid container spacing={2}>
              {/* Fila 1: Estado, Banco, Fecha Desde, Fecha Hasta */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filtros.estado_abono}
                    label="Estado"
                    onChange={(e) =>
                      handleFiltroChange("estado_abono", e.target.value)
                    }
                  >
                    {opcionesEstadoAbono.map((opcion) => (
                      <MenuItem key={opcion.value} value={opcion.value}>
                        {opcion.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Banco</InputLabel>
                  <Select
                    value={filtros.banco}
                    label="Banco"
                    onChange={(e) =>
                      handleFiltroChange("banco", e.target.value)
                    }
                  >
                    {opcionesBanco.map((opcion) => (
                      <MenuItem key={opcion.value} value={opcion.value}>
                        {opcion.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Fecha Desde"
                  value={filtros.fecha_desde}
                  onChange={(newValue) =>
                    handleFiltroChange("fecha_desde", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Fecha Hasta"
                  value={filtros.fecha_hasta}
                  onChange={(newValue) =>
                    handleFiltroChange("fecha_hasta", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                />
              </Grid>

              {/* Fila 2: Monto Min, Monto Max, Botón Limpiar */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monto Mínimo"
                  type="number"
                  placeholder="0"
                  value={filtros.monto_min}
                  onChange={(e) =>
                    handleFiltroChange("monto_min", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <Typography variant="body2" sx={{ mr: 0.5 }}>
                        $
                      </Typography>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monto Máximo"
                  type="number"
                  placeholder="0"
                  value={filtros.monto_max}
                  onChange={(e) =>
                    handleFiltroChange("monto_max", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <Typography variant="body2" sx={{ mr: 0.5 }}>
                        $
                      </Typography>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleLimpiarFiltros}
                  disabled={filtrosActivos === 0}
                  sx={{ height: 40 }}
                >
                  Limpiar Filtros
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* ==================== RESUMEN ==================== */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Total: {pagination.total} abono{pagination.total !== 1 ? "s" : ""}
          </Typography>
        </Box>

        {/* ==================== TABLA ==================== */}
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <TablaHead headLabel={TABLE_HEAD} rowCount={abonos.length} />
            <TableBody>
              {/* LOADING STATE */}
              {(isLoading || isFetching) && (
                <>
                  {[...Array(rowsPerPage)].map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {TABLE_HEAD.map((head) => (
                        <TableCell key={head.id}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </>
              )}

              {/* DATOS REALES */}
              {!isLoading && !isFetching && abonos.length > 0 && (
                <>
                  {abonos.map((abono) => {
                    const movimiento = abono.movimiento || {};
                    const bancoConfig = getBancoConfig(movimiento.banco);
                    const montoBajo = esMontoBajo(abono.monto_abono_pendiente);
                    const esPendiente = abono.estado_abono === "PENDIENTE";

                    return (
                      <TableRow
                        key={abono.id}
                        hover
                        style={
                          montoBajo && esPendiente ? estiloFilaMontoBajo : {}
                        }
                      >
                        {/* Fecha Conciliación */}
                        <TableCell>
                          <Typography variant="body2">
                            {formatearFechaDisplay(abono.fecha_concilia)}
                          </Typography>
                        </TableCell>

                        {/* Banco */}
                        <TableCell>
                          <Chip
                            label={bancoConfig.label}
                            size="small"
                            style={{
                              backgroundColor: bancoConfig.bgColor,
                              color: bancoConfig.color || "#000",
                              fontWeight: 600,
                              fontSize: 11,
                            }}
                          />
                        </TableCell>

                        {/* RUT Ordenante */}
                        <TableCell>
                          <Typography variant="body2">
                            {formatearRutDisplay(movimiento.rut_ordenante)}
                          </Typography>
                          {movimiento.nombre_ordenante && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              {truncarTexto(movimiento.nombre_ordenante, 25)}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Glosa */}
                        <TableCell>
                          <Tooltip title={movimiento.glosa || ""} arrow>
                            <Typography variant="body2">
                              {truncarTexto(movimiento.glosa, 40)}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* Monto Movimiento */}
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 600, color: "#4CAF50" }}
                          >
                            {formatearMonto(movimiento.monto)}
                          </Typography>
                        </TableCell>

                        {/* Abono Disponible */}
                        <TableCell align="right">
                          <Box>
                            <Chip
                              icon={
                                <MonetizationOnIcon
                                  style={{ fontSize: 14, color: "#fff" }}
                                />
                              }
                              label={formatearMonto(
                                abono.monto_abono_pendiente,
                              )}
                              style={{
                                backgroundColor: "#FF9800",
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 13,
                              }}
                            />
                            {/* Chip de monto bajo debajo */}
                            {montoBajo && esPendiente && (
                              <Box mt={0.5}>
                                <Chip
                                  label="Monto bajo"
                                  size="small"
                                  style={{
                                    backgroundColor: "#FFF3E0",
                                    color: "#E65100",
                                    fontSize: 10,
                                    height: 20,
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        {/* Acciones */}
                        <TableCell>
                          {esPendiente ? (
                            <Box display="flex" gap={1}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<MonetizationOnIcon />}
                                onClick={() => handleAplicarAbono(abono)}
                                style={{
                                  backgroundColor: "#FF9800",
                                  color: "#fff",
                                }}
                              >
                                Aplicar
                              </Button>
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenMenu(e, abono)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <Chip
                              label={
                                abono.estado_abono === "APLICADO"
                                  ? "Aplicado"
                                  : abono.estado_abono === "REEMBOLSADO"
                                    ? "Reembolsado"
                                    : "Descartado"
                              }
                              size="small"
                              style={{
                                backgroundColor:
                                  abono.estado_abono === "APLICADO"
                                    ? "#4CAF50"
                                    : abono.estado_abono === "REEMBOLSADO"
                                      ? "#2196F3"
                                      : "#9E9E9E",
                                color: "#fff",
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </>
              )}

              {/* EMPTY STATE */}
              {!isLoading && !isFetching && abonos.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={TABLE_HEAD.length}>
                    <Box sx={{ py: 3 }}>
                      <SearchNotFound searchQuery="" />
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mt: 2 }}
                      >
                        {filtros.estado_abono === "PENDIENTE"
                          ? "No hay abonos pendientes de aplicar"
                          : "No se encontraron abonos con los filtros seleccionados"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ==================== PAGINACIÓN ==================== */}
        {!isLoading && !isFetching && pagination.total > 0 && (
          <TablePagination
            component="div"
            count={pagination.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        )}

        {/* ==================== MENÚ CONTEXTUAL ==================== */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
        >
          <MenuItem
            onClick={() => {
              handleAplicarAbono(menuAbono);
            }}
          >
            <MonetizationOnIcon
              fontSize="small"
              style={{ marginRight: 8, color: "#4CAF50" }}
            />
            Aplicar a Factura
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCerrarAbono(menuAbono);
            }}
          >
            <UndoIcon
              fontSize="small"
              style={{ marginRight: 8, color: "#2196F3" }}
            />
            Reembolsar
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCerrarAbono(menuAbono);
            }}
          >
            <DeleteIcon
              fontSize="small"
              style={{ marginRight: 8, color: "#9E9E9E" }}
            />
            Descartar
          </MenuItem>
        </Menu>

        {/* ==================== MODALES ==================== */}
        <ModalAplicarAbono
          open={modalAplicar.open}
          onClose={handleCloseModalAplicar}
          abono={modalAplicar.abono}
        />

        <ModalCerrarAbono
          open={modalCerrar.open}
          onClose={handleCloseModalCerrar}
          abono={modalCerrar.abono}
        />
      </Card>
    </LocalizationProvider>
  );
}
